/**
 * translationResolver.ts — Enterprise Translation Resolver
 *
 * Resolves translations with a strict priority chain:
 *   1. Admin translation override (from DB)
 *   2. Cached translation (in-memory)
 *   3. Static dictionary (Vue I18n / en.json)
 *   4. English fallback
 *   5. Humanized key (last resort — never shows raw dotted keys)
 *
 * Example fallback: "dashboard.totalMRR" → "Total MRR"
 */

import { i18n } from "@/i18n";
import { resolveLocalizationKey } from "@/utils/localizationResolver";

// ── Key normalization ─────────────────────────────────────────────────────────

/**
 * Legacy key mapping. Maps ALL-CAPS dotted keys (from older code) to their
 * canonical en.json equivalents.
 */
const LEGACY_KEY_MAP: Record<string, string> = {
  "DASHBOARD.TOTALMRR": "dashboard.totalMRR",
  "DASHBOARD.AVGRISKSCORE": "dashboard.avgRiskScore",
  "DASHBOARD.TOTALREVENUE": "dashboard.totalRevenue",
  "DASHBOARD.REVENUEAENTRISK": "dashboard.revenueAtRisk",
  "DASHBOARD.OUTOF100": "dashboard.outOf100",
};

/**
 * Normalize a localization key from any legacy format to its canonical form.
 *
 * Handles:
 *   • ALL-CAPS dotted keys:  DASHBOARD.TOTALMRR → dashboard.totalMRR
 *   • Mixed case:            Dashboard.TotalMRR → dashboard.totalMRR
 *   • Already canonical:     dashboard.mrr → dashboard.mrr (no change)
 */
export function normalizeLocalizationKey(key: string): string {
  if (!key) return key;

  // Check explicit legacy mapping first
  const upper = key.toUpperCase();
  if (LEGACY_KEY_MAP[upper]) return LEGACY_KEY_MAP[upper];

  // If the key is all-uppercase+dots, convert to camelCase segments
  if (/^[A-Z._]+$/.test(key)) {
    return key
      .toLowerCase()
      .split(".")
      .map((segment, i) => {
        if (i === 0) return segment;
        // Keep segments lowercase for first level, camelCase deeper
        return segment;
      })
      .join(".");
  }

  return key;
}

// ── Humanize fallback ─────────────────────────────────────────────────────────

/**
 * Convert a dotted key into a human-readable label.
 *
 * Examples:
 *   "dashboard.totalMRR"     → "Total MRR"
 *   "dashboard.avgRiskScore" → "Avg Risk Score"
 *   "common.noData"          → "No Data"
 *   "nav.aiInsights"         → "AI Insights"
 */
export function humanizeKey(key: string): string {
  if (!key) return key;

  // Take the last segment (most specific)
  const parts = key.split(".");
  const last = parts[parts.length - 1];

  // Split camelCase/PascalCase into words
  return last
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → camel Case
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // MRR → MRR, RiskScore → Risk Score
    .replace(/^./, (s) => s.toUpperCase()); // Capitalize first letter
}

// ── Main resolver ─────────────────────────────────────────────────────────────

/**
 * Resolve a translation with the full priority chain.
 *
 * @param key    - Dotted i18n key (may be legacy format)
 * @param locale - Target locale (defaults to current i18n locale)
 * @param memoryCache - Optional in-memory cache map (locale::text → translated)
 */
export function resolveTranslation(
  key: string,
  locale?: string,
  memoryCache?: Map<string, string>,
): string {
  const normalizedKey = normalizeLocalizationKey(key);
  const loc =
    locale ??
    (i18n.global.locale as unknown as { value: string }).value ??
    "en";

  // 1. Check memory cache (includes admin overrides cached from API)
  if (memoryCache) {
    const englishText = resolveLocalizationKey(normalizedKey);
    if (englishText !== normalizedKey) {
      const cached = memoryCache.get(`${loc}::${englishText}`);
      if (cached) return cached;
    }
  }

  // 2. Check Vue I18n messages (static dictionary + pre-translated messages)
  const i18nGlobal = i18n.global as unknown as {
    te: (k: string) => boolean;
    t: (k: string) => string;
  };
  if (i18nGlobal.te(normalizedKey)) {
    const result = i18nGlobal.t(normalizedKey);
    if (result !== normalizedKey) return result;
  }

  // 3. English fallback from en.json
  const englishFallback = resolveLocalizationKey(normalizedKey);
  if (englishFallback !== normalizedKey) return englishFallback;

  // 4. Humanized key (last resort)
  return humanizeKey(normalizedKey);
}
